import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useCreateDispute } from '@/api-services/hooks/useDispute';
import { DisputeDTO, DisputeType, DisputeStatus, DisputePriority, RaisedByType } from '@/api-services/hooks/disputeTypes';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface DisputeFormData {
  bookingId: string;
  disputeType: DisputeType | '';
  reason: string;
  description: string;
  raisedByType: RaisedByType | '';
  raisedById: string;
  status: DisputeStatus | '';
  priority: DisputePriority | '';
}

const AddDisputePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<DisputeFormData>({
    bookingId: '',
    disputeType: '',
    reason: '',
    description: '',
    raisedByType: '',
    raisedById: '',
    status: 'OPEN',
    priority: 'MEDIUM',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof DisputeFormData, string>>>({});

  const createDisputeMutation = useCreateDispute({
    onSuccess: () => {
      // Invalidate queries - React Query will automatically refetch when DisputesPage mounts
      queryClient.invalidateQueries({ queryKey: ['disputes'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-stats'] });
      
      toast.success('Dispute created successfully!');
      navigate('/admin/disputes');
    },
    onError: (error) => {
      toast.error(`Failed to create dispute: ${error.message}`);
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof DisputeFormData, string>> = {};

    if (!formData.bookingId.trim()) {
      newErrors.bookingId = 'Booking ID is required';
    } else if (isNaN(Number(formData.bookingId)) || Number(formData.bookingId) <= 0) {
      newErrors.bookingId = 'Booking ID must be a valid positive number';
    }

    if (!formData.disputeType) {
      newErrors.disputeType = 'Dispute type is required';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    } else if (formData.reason.trim().length < 3) {
      newErrors.reason = 'Reason must be at least 3 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    if (!formData.raisedByType) {
      newErrors.raisedByType = 'Raised by type is required';
    }

    if (!formData.raisedById.trim()) {
      newErrors.raisedById = 'Raised by ID is required';
    } else if (isNaN(Number(formData.raisedById)) || Number(formData.raisedById) <= 0) {
      newErrors.raisedById = 'Raised by ID must be a valid positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DisputeFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    const disputeData: DisputeDTO = {
      bookingId: Number(formData.bookingId),
      disputeType: formData.disputeType as DisputeType,
      reason: formData.reason.trim(),
      description: formData.description.trim(),
      raisedByType: formData.raisedByType as RaisedByType,
      raisedById: Number(formData.raisedById),
      status: formData.status ? (formData.status as DisputeStatus) : 'OPEN',
      priority: formData.priority ? (formData.priority as DisputePriority) : 'MEDIUM',
    };

    createDisputeMutation.mutate(disputeData);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/disputes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create Dispute</h1>
            <p className="text-muted-foreground mt-1">Create a new dispute record</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Dispute Information</CardTitle>
            <CardDescription>Enter the details of the dispute</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Booking ID */}
            <div className="space-y-2">
              <Label htmlFor="bookingId">
                Booking ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="bookingId"
                type="number"
                placeholder="Enter booking ID"
                value={formData.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                className={errors.bookingId ? 'border-destructive' : ''}
              />
              {errors.bookingId && (
                <p className="text-sm text-destructive">{errors.bookingId}</p>
              )}
            </div>

            {/* Dispute Type */}
            <div className="space-y-2">
              <Label htmlFor="disputeType">
                Dispute Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.disputeType}
                onValueChange={(value) => handleInputChange('disputeType', value)}
              >
                <SelectTrigger className={errors.disputeType ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select dispute type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAYMENT">Payment</SelectItem>
                  <SelectItem value="DELIVERY">Delivery</SelectItem>
                  <SelectItem value="PRODUCT">Product</SelectItem>
                  <SelectItem value="REFUND">Refund</SelectItem>
                </SelectContent>
              </Select>
              {errors.disputeType && (
                <p className="text-sm text-destructive">{errors.disputeType}</p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason <span className="text-destructive">*</span>
              </Label>
              <Input
                id="reason"
                placeholder="Enter dispute reason"
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                className={errors.reason ? 'border-destructive' : ''}
              />
              {errors.reason && (
                <p className="text-sm text-destructive">{errors.reason}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                placeholder="Enter detailed description of the dispute"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={5}
                className={errors.description ? 'border-destructive' : ''}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description}</p>
              )}
            </div>

            {/* Raised By Type */}
            <div className="space-y-2">
              <Label htmlFor="raisedByType">
                Raised By Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.raisedByType}
                onValueChange={(value) => handleInputChange('raisedByType', value)}
              >
                <SelectTrigger className={errors.raisedByType ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select who raised the dispute" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RIDER">Rider</SelectItem>
                  <SelectItem value="DRIVER">Driver</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.raisedByType && (
                <p className="text-sm text-destructive">{errors.raisedByType}</p>
              )}
            </div>

            {/* Raised By ID */}
            <div className="space-y-2">
              <Label htmlFor="raisedById">
                Raised By ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="raisedById"
                type="number"
                placeholder="Enter user ID (rider/driver/admin ID)"
                value={formData.raisedById}
                onChange={(e) => handleInputChange('raisedById', e.target.value)}
                className={errors.raisedById ? 'border-destructive' : ''}
              />
              {errors.raisedById && (
                <p className="text-sm text-destructive">{errors.raisedById}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/disputes')}
                disabled={createDisputeMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createDisputeMutation.isPending}>
                {createDisputeMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Dispute
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default AddDisputePage;
